import { TELEMETRY_TEXT_MAX } from "../limits.ts";
import { getTelemetry } from "./client.ts";
import {
  EmbajadoresEvents as E,
  type ChatSurfaceName,
  type WorkSection,
} from "./events.ts";

/**
 * Los eventos de producto, con sus propiedades. La capa de negocio sobre
 * `@lala/telemetry-sdk`, como `analytics.ts` en nesforce-admin-ui.
 *
 *     import { analytics } from "@/lib/telemetry";
 *     analytics.nav.modeSwitched({ from: "chat", to: "work", … });
 *
 * Quién es (actor) y lo que vale para todos los eventos --app, entorno,
 * versión, roles, nivel de acceso, modo-- lo pone `TelemetryBridge` una vez;
 * aquí no se repite.
 *
 * DOS REGLAS:
 *
 * 1. Se manda el contenido: el texto de los mensajes y las búsquedas, los
 *    títulos, los nombres de archivo y los datos del registro de Work, porque
 *    así se decidió para poder analizar el uso completo (#5). Solo se corta lo
 *    muy largo, a `TELEMETRY_TEXT_MAX` (`lib/limits.ts`).
 * 2. Nunca rompe la aplicación. Todo va en `try/catch`: si el SDK lanza o la
 *    ingesta está caída, la interfaz ni se entera.
 */

type Value =
  | string
  | number
  | boolean
  | null
  | readonly Value[]
  | { readonly [key: string]: Value | undefined };
type Props = Readonly<Record<string, Value | undefined>>;

/** Un texto, cortado si pasa del límite. */
export const truncate = (text: string): string =>
  text.length <= TELEMETRY_TEXT_MAX
    ? text
    : `${text.slice(0, TELEMETRY_TEXT_MAX - 1)}…`;

/** Sin `undefined`, y con los textos largos cortados, a cualquier nivel. */
const cleanValue = (value: Value): Value => {
  if (typeof value === "string") return truncate(value);
  if (Array.isArray(value)) return (value as readonly Value[]).map(cleanValue);
  if (value !== null && typeof value === "object") {
    return clean(value as Props);
  }
  return value;
};

const clean = (props: Props): Record<string, Value> => {
  const out: Record<string, Value> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = cleanValue(value);
  }
  return out;
};

/**
 * El registro de Work al que le pasó algo. Un participante lleva id, nombre,
 * correo, teléfono, cargo y región; un cargo o una región, id, código y
 * nombre.
 */
export type WorkRecord = {
  readonly recordId?: string | undefined;
  readonly name?: string | undefined;
  readonly code?: string | undefined;
  readonly email?: string | null | undefined;
  readonly phone?: string | null | undefined;
  readonly position?: string | null | undefined;
  readonly region?: string | null | undefined;
};

/** El emisor de todo: tolerante a fallos. */
const track = (type: string, props: Props = {}): void => {
  try {
    getTelemetry()?.track(type, clean(props));
  } catch {
    // La telemetría no puede tirar la aplicación.
  }
};

// ---------------------------------------------------------------------------
// Tramos: cuánto, sin decir exactamente cuánto
// ---------------------------------------------------------------------------

/** El largo de un mensaje, en tramos, para agrupar sin leer el texto. */
export const lengthBucket = (length: number): string =>
  length <= 20
    ? "0-20"
    : length <= 100
      ? "21-100"
      : length <= 500
        ? "101-500"
        : length <= 2000
          ? "501-2000"
          : "2000+";

/** El tamaño de un archivo, en tramos de KB. */
export const sizeKbBucket = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb < 100
    ? "<100"
    : kb < 1024
      ? "100-1024"
      : kb < 10240
        ? "1024-10240"
        : "10240+";
};

/** La extensión de un archivo, para agrupar por tipo. */
export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot <= 0
    ? ""
    : fileName
        .slice(dot + 1)
        .toLowerCase()
        .slice(0, 10);
};

// ---------------------------------------------------------------------------
// Cuándo empezó a usarse
// ---------------------------------------------------------------------------

let readyAt: number | null = null;
let firstActionDone = false;

const now = (): number =>
  typeof performance === "undefined" ? Date.now() : performance.now();

/** La primera cosa que hace la persona tras cargar, una sola vez. */
const firstAction = (action: string): void => {
  if (firstActionDone || readyAt === null) return;
  firstActionDone = true;
  track(E.APP_FIRST_ACTION, {
    action,
    msSinceReady: Math.round(now() - readyAt),
  });
};

/** Solo para las pruebas: vuelve a "recién cargada". */
export const resetFirstActionForTesting = (): void => {
  readyAt = null;
  firstActionDone = false;
};

// ---------------------------------------------------------------------------
// Los helpers
// ---------------------------------------------------------------------------

type Mode = "chat" | "work";
type Direction = "asc" | "desc";

export const analytics = {
  auth: {
    /**
     * No se pudo entrar. `stage` dice dónde: `config` (faltan variables),
     * `restore` (el refresh del arranque), `me` (quién eres), `callback` (la
     * vuelta del SSO), `loop` (el anti-bucle paró otro intento).
     */
    signInFailed: (p: {
      stage: "config" | "restore" | "me" | "callback" | "loop";
      status?: number;
      code?: string;
      /** Lo que se le dijo a la persona. */
      reason?: string;
    }) => {
      track(E.AUTH_SIGN_IN_FAILED, p);
    },
    sessionExpired: () => {
      track(E.AUTH_SESSION_EXPIRED);
    },
    /** Con sesión en el SSO pero sin `embajadores:global.view`. */
    accessDenied: () => {
      track(E.AUTH_ACCESS_DENIED);
    },
    logout: (p: {
      origin: "user_menu" | "settings" | "access_denied" | "sign_in_failed";
    }) => {
      track(E.AUTH_LOGOUT, p);
    },
  },

  app: {
    /** Hay sesión y se puede usar. Una vez por carga. */
    ready: () => {
      if (readyAt !== null) return;
      readyAt = now();
      track(E.APP_READY, { msToReady: Math.round(readyAt) });
    },
    /** Un fallo del API visto por TanStack Query. `key`: el primer tramo. */
    apiError: (p: {
      status: number;
      code: string;
      source: "query" | "mutation";
      key?: string;
      reason?: string;
    }) => {
      track(E.APP_API_ERROR, p);
    },
  },

  nav: {
    modeSwitched: (p: {
      from: Mode;
      to: Mode;
      turnBusy: boolean;
      pendingDecisions: number;
    }) => {
      track(E.NAV_MODE_SWITCHED, p);
      firstAction("mode_switched");
    },
    sectionClicked: (p: { section: WorkSection }) => {
      track(E.NAV_SECTION_CLICKED, p);
      firstAction("section_clicked");
    },
    sidebarToggled: (p: { visible: boolean }) => {
      track(E.NAV_SIDEBAR_TOGGLED, p);
    },
    sidebarPanelChanged: (p: { panel: "chats" | "memoria" }) => {
      track(E.NAV_SIDEBAR_PANEL_CHANGED, p);
    },
    settingsOpened: (p: { mode: Mode }) => {
      track(E.NAV_SETTINGS_OPENED, p);
    },
    usageMeterClicked: () => {
      track(E.NAV_USAGE_METER_CLICKED);
    },
  },

  settings: {
    themeChanged: (p: { theme: string }) => {
      track(E.SETTINGS_THEME_CHANGED, p);
    },
  },

  chat: {
    conversationNew: (p: { origin: "sidebar" | "alert" | "bubble" }) => {
      track(E.CHAT_CONVERSATION_NEW, p);
    },
    conversationOpened: (p: {
      origin: "history" | "url";
      conversationId: string;
      messageCount: number;
      title: string | null;
    }) => {
      track(E.CHAT_CONVERSATION_OPENED, p);
      if (p.origin === "history") firstAction("conversation_opened");
    },
    conversationOpenFailed: (p: {
      conversationId: string;
      status?: number;
      code?: string;
      reason?: string;
    }) => {
      track(E.CHAT_CONVERSATION_OPEN_FAILED, p);
    },
    conversationRenamed: (p: {
      conversationId: string;
      previousTitle: string | null;
      title: string;
    }) => {
      track(E.CHAT_CONVERSATION_RENAMED, p);
    },
    conversationDeleted: (p: {
      conversationId: string;
      title?: string | null;
      wasCurrent: boolean;
    }) => {
      track(E.CHAT_CONVERSATION_DELETED, p);
    },

    /** Un mensaje del usuario, con su texto (sin el prefacio de adjuntos). */
    messageSent: (p: {
      text: string;
      attachmentNames?: readonly string[];
      conversationId?: string | undefined;
      isNewConversation: boolean;
      turnIndex: number;
      surface: ChatSurfaceName;
      origin: "composer" | "alert" | "mechanic";
      inputMethod: "typed" | "voice";
      attachmentCount: number;
      lengthBucket: string;
    }) => {
      track(E.CHAT_MESSAGE_SENT, p);
      firstAction("message_sent");
    },
    turnCompleted: (p: {
      conversationId?: string | undefined;
      kind: "message" | "confirmation" | "answer";
      durationMs: number;
      ttfbMs?: number;
      toolNames: readonly string[];
      toolErrorCount: number;
      components: readonly string[];
      hasConfirmation: boolean;
      hasQuestion: boolean;
      fileCount: number;
      isAbort: boolean;
      isError: boolean;
      finishReason?: string;
      /** Lo que respondió el agente, y lo que preguntó si preguntó algo. */
      responseText: string;
      questions?: readonly string[];
    }) => {
      track(E.CHAT_TURN_COMPLETED, p);
    },
    turnFailed: (p: {
      conversationId?: string | undefined;
      status?: number;
      code?: string;
      errorId?: string;
      reason?: string;
    }) => {
      track(E.CHAT_TURN_FAILED, p);
    },
    turnStopped: (p: {
      conversationId?: string | undefined;
      elapsedMs?: number;
    }) => {
      track(E.CHAT_TURN_STOPPED, p);
    },
    errorDismissed: () => {
      track(E.CHAT_ERROR_DISMISSED);
    },

    confirmationDecided: (p: {
      conversationId?: string | undefined;
      actionId: string;
      decision: "approve" | "approve_and_remember" | "reject";
      toolName?: string;
      toolNames?: readonly string[];
      itemCount: number;
      grouped: boolean;
      decisionLatencyMs?: number;
      surface: ChatSurfaceName;
      /** Lo que la tarjeta decía que se iba a hacer. */
      summary?: string;
    }) => {
      track(E.CHAT_CONFIRMATION_DECIDED, p);
    },
    confirmationResult: (p: {
      conversationId?: string | undefined;
      actionId: string;
      outcome: "approved" | "approved_and_remembered" | "rejected" | "expired";
      doneCount: number;
      failedCount: number;
      errors?: readonly string[];
    }) => {
      track(E.CHAT_CONFIRMATION_RESULT, p);
    },
    questionsAnswered: (p: {
      conversationId?: string | undefined;
      questionCount: number;
      customCount: number;
      hasNote: boolean;
      latencyMs?: number;
      surface: ChatSurfaceName;
      /** Qué preguntó el agente y qué se le contestó. */
      prompt?: string | null;
      answers: readonly {
        readonly question: string;
        readonly selected: readonly string[];
        readonly custom?: string;
      }[];
      note?: string;
    }) => {
      track(E.CHAT_QUESTIONS_ANSWERED, p);
    },
    abandonPromptShown: (p: {
      turnBusy: boolean;
      pendingDecisions: number;
    }) => {
      track(E.CHAT_ABANDON_PROMPT_SHOWN, p);
    },
    abandonPromptResolved: (p: { choice: "stay" | "new_conversation" }) => {
      track(E.CHAT_ABANDON_PROMPT_RESOLVED, p);
    },

    attachmentUploaded: (p: {
      ext: string;
      sizeKbBucket: string;
      fileName: string;
      sizeBytes: number;
      attachmentId: string;
    }) => {
      track(E.CHAT_ATTACHMENT_UPLOADED, p);
    },
    attachmentFailed: (p: {
      ext: string;
      fileName: string;
      status?: number;
      code?: string;
      reason?: string;
    }) => {
      track(E.CHAT_ATTACHMENT_FAILED, p);
    },
    attachmentRejected: (p: {
      ext: string;
      fileName: string;
      sizeBytes: number;
      cause: "too_large" | "type" | "count";
    }) => {
      track(E.CHAT_ATTACHMENT_REJECTED, p);
    },
    attachmentRemoved: (p: { ext: string; fileName: string }) => {
      track(E.CHAT_ATTACHMENT_REMOVED, p);
    },

    fileDownloaded: (p: {
      ext: string;
      fileName: string;
      mimeType?: string;
      rowCount?: number;
    }) => {
      track(E.CHAT_FILE_DOWNLOADED, p);
    },
    resourceLinkOpened: (p: {
      resourceType: string;
      resourceId?: string;
      label: string;
    }) => {
      track(E.CHAT_RESOURCE_LINK_OPENED, p);
    },
    tableMoreLoaded: (p: { datasetId?: string }) => {
      track(E.CHAT_TABLE_MORE_LOADED, p);
    },
    openInChat: (p: { render?: string }) => {
      track(E.CHAT_OPEN_IN_CHAT, p);
    },
    componentUnknown: (p: { render: string }) => {
      track(E.CHAT_COMPONENT_UNKNOWN, p);
    },
    bubbleOpened: (p: { pendingDecisions: number }) => {
      track(E.CHAT_BUBBLE_OPENED, p);
    },
    bubbleClosed: () => {
      track(E.CHAT_BUBBLE_CLOSED);
    },
  },

  voice: {
    started: (p: { kind: string }) => {
      track(E.VOICE_STARTED, p);
    },
    completed: (p: { kind: string; durationMs?: number }) => {
      track(E.VOICE_COMPLETED, p);
    },
    cancelled: (p: { kind: string }) => {
      track(E.VOICE_CANCELLED, p);
    },
    failed: (p: { kind: string; code?: string }) => {
      track(E.VOICE_FAILED, p);
    },
  },

  memory: {
    noteOpened: (p: { scope: string; slug: string; summary?: string }) => {
      track(E.MEMORY_NOTE_OPENED, p);
    },
    noteSaved: (p: { scope: string; slug: string; summary?: string }) => {
      track(E.MEMORY_NOTE_SAVED, p);
    },
    noteDeleted: (p: { scope: string; slug: string; summary?: string }) => {
      track(E.MEMORY_NOTE_DELETED, p);
    },
  },

  mechanic: {
    opened: (p: { origin: "memory"; period: string }) => {
      track(E.MECHANIC_OPENED, p);
    },
    listExpanded: () => {
      track(E.MECHANIC_LIST_EXPANDED);
    },
    editStarted: (p: { period?: string }) => {
      track(E.MECHANIC_EDIT_STARTED, p);
    },
    diffPreviewed: (p: { period?: string }) => {
      track(E.MECHANIC_DIFF_PREVIEWED, p);
    },
    handedToAgent: (p: { period?: string }) => {
      track(E.MECHANIC_HANDED_TO_AGENT, p);
    },
  },

  work: {
    periodChanged: (p: {
      section: WorkSection;
      period: string;
      wholeYear: boolean;
    }) => {
      track(E.WORK_PERIOD_CHANGED, p);
      firstAction("period_changed");
    },
    alertAction: (p: {
      section: WorkSection;
      alert: string;
      action: string;
    }) => {
      track(E.WORK_ALERT_ACTION, p);
    },
    overviewExpanded: (p: { section: WorkSection; block: string }) => {
      track(E.WORK_OVERVIEW_EXPANDED, p);
    },
    batchOpened: (p: { section: WorkSection; origin: string }) => {
      track(E.WORK_BATCH_OPENED, p);
    },
    /** Una búsqueda, con lo que se escribió y cuántos salieron. */
    search: (p: {
      section: WorkSection;
      hasQuery: boolean;
      query: string;
      resultCount?: number;
      filters?: readonly string[];
    }) => {
      track(E.WORK_SEARCH, p);
    },
    listMoreLoaded: (p: { section: WorkSection; loaded?: number }) => {
      track(E.WORK_LIST_MORE_LOADED, p);
    },
    recordOpened: (p: { section: WorkSection } & WorkRecord) => {
      track(E.WORK_RECORD_OPENED, p);
    },
    recordCreateStarted: (p: { section: WorkSection }) => {
      track(E.WORK_RECORD_CREATE_STARTED, p);
    },
    recordSaved: (
      p: { section: WorkSection; mode: "create" | "update" } & WorkRecord,
    ) => {
      track(E.WORK_RECORD_SAVED, p);
    },
    recordDeleted: (
      p: { section: WorkSection; inUse?: boolean } & WorkRecord,
    ) => {
      track(E.WORK_RECORD_DELETED, p);
    },
    participantActiveToggled: (
      p: { section: WorkSection; active: boolean } & WorkRecord,
    ) => {
      track(E.WORK_PARTICIPANT_ACTIVE_TOGGLED, p);
    },
    enrollmentSaved: (
      p: {
        section: WorkSection;
        mode: "create" | "update";
        year: number;
        month?: number;
        agency?: string;
        positionId: string;
        regionId?: string;
      } & WorkRecord,
    ) => {
      track(E.WORK_ENROLLMENT_SAVED, p);
    },
    tableSorted: (p: {
      section: WorkSection;
      grid: string;
      column: string;
      direction: Direction | "none";
    }) => {
      track(E.WORK_TABLE_SORTED, p);
    },
    pricesSynced: (p: { section: WorkSection; ok: boolean }) => {
      track(E.WORK_PRICES_SYNCED, p);
    },
    permissionBlocked: (p: { section: WorkSection | "unknown" }) => {
      track(E.WORK_PERMISSION_BLOCKED, p);
    },
  },

  /** Para lo que no encaje en ningún helper. Corta lo largo igual. */
  track,
};
